// asyncStore.ts
import { AxiosResponse } from 'axios';
import { atom } from 'jotai';
import { getDefaultStore } from 'jotai';
import { onPromise, PromiseOptions } from './onPromise';

export const AsyncStatus = {
	default: 'default',
	pending: 'pending',
	ready: 'ready',
	error: 'error',
} as const;

type AsyncStatus = typeof AsyncStatus[keyof typeof AsyncStatus];

interface AsyncState {
	status: AsyncStatus;
	data: any;
	error: any;
	headers: any;
}

// 초기 상태 정의
const initialState: AsyncState = {
	status: AsyncStatus.default,
	data: null,
	error: null,
	headers: null,
};

// 메인 atom 생성 함수
export function createAsyncStore() {
	// 내부용 상태 저장 atom
	const baseAtom = atom<AsyncState>({ ...initialState });

	const actions = {
		onDefault: () => (getDefaultStore().set(baseAtom, { ...initialState })),
		onPending: () => {
			getDefaultStore().set(baseAtom, (prev) => ({
				...prev,
				status: AsyncStatus.pending,
				error: null,
			}))
		},
		onReady: (data: any, headers?: any) => {
			getDefaultStore().set(baseAtom, {
				status: AsyncStatus.ready,
				data,
				headers: headers || null,
				error: null,
			})
		},
		onMore: (data: any, datakey: string, headers?: any) =>
			getDefaultStore().set(baseAtom, (prev) => ({
				status: AsyncStatus.ready,
				data: {
					...prev.data,
					[datakey]: [...(prev.data?.[datakey] || []), ...data[datakey]],
				},
				headers: headers || null,
				error: null,
			})),

		onError: (error: any, headers?: any) => {
			getDefaultStore().set(baseAtom, {
				status: AsyncStatus.error,
				data: null,
				headers: headers || null,
				error: error,
			})
		},

		onGetAll: (promise: () => Promise<AxiosResponse>, options?: PromiseOptions) => {
			onPromise(actions)(promise, options);
		},
	};

	// 외부 노출용: 상태 + 액션 + 편의속성 합침
	const stateAtom = atom((get) => {
		const state = get(baseAtom);
		return {
			...state,
			...actions,
			isPending: state.status === AsyncStatus.pending,
			isReady: state.status === AsyncStatus.ready,
			isError: state.status === AsyncStatus.error,
			isDefault: state.status === AsyncStatus.default,
		};
	});

	return {
		stateAtom,
		...actions,
	};
}
